/**
 * Riders Store (Zustand)
 * 
 * Global riders state management
 * - Riders list with loading and error states
 * - CRUD operations (fetch, create, update, delete)
 * - Season filtering logic
 * - Selected rider tracking
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.5
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { restClient, Rider, CreateRiderDto, UpdateRiderDto, RiderStats } from '../api/rest-client';

interface RidersState {
  // State
  riders: Rider[];
  selectedRider: Rider | null;
  selectedRiderStats: RiderStats | null;
  selectedSeason: string | null;
  loading: boolean;
  error: string | null;

  // Computed
  filteredRiders: Rider[];

  // Actions
  fetchRiders: (season?: string) => Promise<void>;
  selectRider: (id: string, forceRefresh?: boolean) => Promise<void>;
  fetchRiderStats: (riderId: string, season: string) => Promise<void>;
  createRider: (rider: CreateRiderDto) => Promise<Rider>;
  updateRider: (id: string, rider: UpdateRiderDto) => Promise<Rider>;
  deleteRider: (id: string) => Promise<void>;
  setSelectedSeason: (season: string | null) => void;
  clearError: () => void;
  clearSelectedRider: () => void;
}

/**
 * Get current season (current year as string)
 */
function getCurrentSeason(): string {
  return new Date().getFullYear().toString();
}

/**
 * Riders Store
 * 
 * Manages riders state globally using Zustand
 */
export const useRidersStore = create<RidersState>()(
  devtools(
    (set, get) => ({
      // Initial state
      riders: [],
      selectedRider: null,
      selectedRiderStats: null,
      selectedSeason: getCurrentSeason(),
      loading: false,
      error: null,

      // Computed property - filtered riders by season
      get filteredRiders() {
        const { riders, selectedSeason } = get();
        
        if (!selectedSeason) {
          return riders;
        }

        // Check if the rider's season matches OR seasons_active includes the selected season (OR logic)
        return riders.filter(rider => {
          // Either primary season matches OR seasons_active contains selected season
          return rider.season === selectedSeason || 
            (rider.seasons_active && rider.seasons_active.includes(selectedSeason));
        });
      },

      /**
       * Fetch riders from API
       * Requirements: 4.1, 4.2, 4.3
       */
      fetchRiders: async () => {
        set({ loading: true, error: null });

        try {
          // Always fetch all riders (no season filter on API)
          // Frontend will filter by seasons_active
          const riders = await restClient.getRiders();

          set({
            riders,
            loading: false,
            error: null,
          });
        } catch (error) {
          console.error('Failed to fetch riders:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch riders',
            loading: false,
          });
        }
      },

      /**
       * Select and fetch a single rider by ID
       * Requirements: 4.1
       */
      selectRider: async (id: string, forceRefresh: boolean = false) => {
        set({ loading: true, error: null });

        try {
          // Check if rider is already in the list (unless force refresh)
          if (!forceRefresh) {
            const existingRider = get().riders.find(r => r.rider_id === id);
            
            if (existingRider) {
              set({
                selectedRider: existingRider,
                loading: false,
              });
              return;
            }
          }

          // Fetch from API if not in list or force refresh
          const rider = await restClient.getRider(id);

          set({
            selectedRider: rider,
            loading: false,
            error: null,
          });
        } catch (error) {
          console.error('Failed to fetch rider:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch rider',
            loading: false,
          });
        }
      },

      /**
       * Fetch rider statistics for a specific season
       * Requirements: 4.8
       */
      fetchRiderStats: async (riderId: string, season: string) => {
        set({ loading: true, error: null });

        try {
          const stats = await restClient.getRiderStats(riderId, season);

          set({
            selectedRiderStats: stats,
            loading: false,
            error: null,
          });
        } catch (error) {
          console.error('Failed to fetch rider stats:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch rider stats',
            loading: false,
          });
        }
      },

      /**
       * Create a new rider
       * Requirements: 4.5
       */
      createRider: async (riderDto: CreateRiderDto) => {
        set({ loading: true, error: null });

        try {
          const newRider = await restClient.createRider(riderDto);

          // Add to riders list
          set(state => ({
            riders: [...state.riders, newRider],
            loading: false,
            error: null,
          }));

          return newRider;
        } catch (error) {
          console.error('Failed to create rider:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to create rider',
            loading: false,
          });
          throw error;
        }
      },

      /**
       * Update an existing rider
       * Requirements: 4.6
       */
      updateRider: async (id: string, riderDto: UpdateRiderDto) => {
        set({ loading: true, error: null });

        try {
          await restClient.updateRider(id, riderDto);

          // Merge submitted data with existing rider in the store
          // (API returns {message, rider_id}, not a full rider object)
          set(state => {
            const existingRider = state.riders.find(r => r.rider_id === id);
            const mergedRider = existingRider 
              ? { ...existingRider, ...riderDto, rider_id: id } as Rider
              : { ...riderDto, rider_id: id } as Rider;

            return {
              riders: state.riders.map(r => 
                r.rider_id === id ? mergedRider : r
              ),
              selectedRider: state.selectedRider?.rider_id === id 
                ? mergedRider 
                : state.selectedRider,
              loading: false,
              error: null,
            };
          });

          return get().riders.find(r => r.rider_id === id) as Rider;
        } catch (error) {
          console.error('Failed to update rider:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to update rider',
            loading: false,
          });
          throw error;
        }
      },

      /**
       * Delete a rider
       * Requirements: 4.7
       */
      deleteRider: async (id: string) => {
        set({ loading: true, error: null });

        try {
          await restClient.deleteRider(id);

          // Remove from riders list
          set(state => ({
            riders: state.riders.filter(r => r.rider_id !== id),
            selectedRider: state.selectedRider?.rider_id === id 
              ? null 
              : state.selectedRider,
            loading: false,
            error: null,
          }));
        } catch (error) {
          console.error('Failed to delete rider:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to delete rider',
            loading: false,
          });
          throw error;
        }
      },

      /**
       * Set selected season for filtering
       * Requirements: 4.2, 4.3
       */
      setSelectedSeason: (season: string | null) => {
        set({ selectedSeason: season });
      },

      /**
       * Clear error state
       */
      clearError: () => {
        set({ error: null });
      },

      /**
       * Clear selected rider
       */
      clearSelectedRider: () => {
        set({ 
          selectedRider: null,
          selectedRiderStats: null,
        });
      },
    }),
    { name: 'RidersStore' }
  )
);

// Selectors for convenience
export const selectRiders = (state: RidersState) => state.riders;
export const selectFilteredRiders = (state: RidersState) => state.filteredRiders;
export const selectSelectedRider = (state: RidersState) => state.selectedRider;
export const selectSelectedRiderStats = (state: RidersState) => state.selectedRiderStats;
export const selectSelectedSeason = (state: RidersState) => state.selectedSeason;
export const selectRidersLoading = (state: RidersState) => state.loading;
export const selectRidersError = (state: RidersState) => state.error;

export default useRidersStore;
